/**
 * Folder View Header - Toolbar with controls
 */

import { useEffect, useRef, useState } from 'react';
import {
  LayoutGrid,
  List,
  Plus,
  Table,
  Link2,
  FolderPlus,
  ArrowUpDown,
  Filter,
  Trash2,
  Search,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  Input,
} from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';

import type { ViewMode, SortBy, SortDir, WorkspaceItem } from './types';

import { useDebounce } from '@/hooks/useDebounce';

interface FolderViewHeaderProps {
  folderName?: string;
  items: WorkspaceItem[];
  filteredCount: number;
  totalCount: number;
  viewMode: ViewMode;
  sortBy: SortBy;
  sortDir: SortDir;
  typeFilter: WorkspaceItemType | 'all';
  searchQuery: string;
  isTrash?: boolean;
  isEmptyingTrash?: boolean;
  /** Whether user can edit (editor/owner) - viewers cannot create/modify items */
  canEdit?: boolean;
  onViewModeChange: (mode: ViewMode) => void;
  onSortByChange: (sort: SortBy) => void;
  onSortDirChange: (dir: SortDir) => void;
  onTypeFilterChange: (type: WorkspaceItemType | 'all') => void;
  onSearchQueryChange: (query: string) => void;
  onCreateTable?: () => void;
  onCreateLinkedTable?: () => void;
  onCreateFolder?: () => void;
  onEmptyTrash?: () => void;
}

export function FolderViewHeader({
  folderName,
  items,
  filteredCount,
  totalCount,
  viewMode,
  sortBy,
  sortDir,
  typeFilter,
  searchQuery,
  isTrash = false,
  isEmptyingTrash = false,
  canEdit = true,
  onViewModeChange,
  onSortByChange,
  onSortDirChange,
  onTypeFilterChange,
  onSearchQueryChange,
  onCreateTable,
  onCreateLinkedTable,
  onCreateFolder,
  onEmptyTrash,
}: FolderViewHeaderProps) {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const debouncedSearchQuery = useDebounce(localSearchQuery, 300);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update parent when debounced value changes
  useEffect(() => {
    onSearchQueryChange(debouncedSearchQuery);
  }, [debouncedSearchQuery, onSearchQueryChange]);

  // Sync local state with prop changes (for clearing from outside)
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl+F to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle clear search
  const handleClearSearch = () => {
    setLocalSearchQuery('');
    searchInputRef.current?.blur();
  };

  // Handle escape key in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClearSearch();
    }
  };

  return (
    <div className="mb-4 flex items-center justify-between gap-4">
      {/* Left side: Title and count */}
      <div className="flex-shrink-0">
        <h2 className="text-lg font-semibold">{folderName || 'All Items'}</h2>
        <p className="text-muted-foreground text-sm">
          {searchQuery ? (
            <>
              {filteredCount} of {totalCount} item{totalCount !== 1 ? 's' : ''}
            </>
          ) : (
            <>
              {filteredCount} item{filteredCount !== 1 ? 's' : ''}
              {typeFilter !== 'all' && ` (filtered)`}
            </>
          )}
        </p>
      </div>

      {/* Middle: Search and View mode */}
      <div className="flex flex-1 max-w-md items-center gap-2">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search items..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="h-8 pl-9 pr-9"
          />
          {localSearchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute right-1 top-1/2 size-7 -translate-y-1/2 p-0"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex border rounded-md shrink-0">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-r-none"
            onClick={() => onViewModeChange('grid')}
          >
            <LayoutGrid className="size-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-l-none"
            onClick={() => onViewModeChange('list')}
          >
            <List className="size-4" />
          </Button>
        </div>
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-2">
        {/* Filter dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="mr-2 size-4" />
              Filter by type
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={typeFilter === 'all'}
              onCheckedChange={() => onTypeFilterChange('all')}
            >
              All Types
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={typeFilter === 'table'}
              onCheckedChange={() =>
                onTypeFilterChange(typeFilter === 'table' ? 'all' : 'table')
              }
            >
              <Table className="mr-2 size-4" />
              Tables
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={typeFilter === 'linked_table'}
              onCheckedChange={() =>
                onTypeFilterChange(typeFilter === 'linked_table' ? 'all' : 'linked_table')
              }
            >
              <Link2 className="mr-2 size-4" />
              Linked Tables
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={typeFilter === 'folder'}
              onCheckedChange={() =>
                onTypeFilterChange(typeFilter === 'folder' ? 'all' : 'folder')
              }
            >
              <FolderPlus className="mr-2 size-4 fill-amber-500 text-amber-600" />
              Folders
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sort dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <ArrowUpDown className="mr-2 size-4" />
              Sort
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={sortBy === 'updated_at'}
              onCheckedChange={() => onSortByChange('updated_at')}
            >
              Last Updated
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortBy === 'created_at'}
              onCheckedChange={() => onSortByChange('created_at')}
            >
              Date Created
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortBy === 'name'}
              onCheckedChange={() => onSortByChange('name')}
            >
              Name
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortBy === 'type'}
              onCheckedChange={() => onSortByChange('type')}
            >
              Type
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={sortDir === 'desc'}
              onCheckedChange={() => onSortDirChange(sortDir === 'desc' ? 'asc' : 'desc')}
            >
              {sortDir === 'desc' ? 'Descending' : 'Ascending'}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Empty Trash button (only in trash view) */}
        {isTrash && items.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={isEmptyingTrash || !canEdit}
                >
                  <Trash2 className="mr-2 size-4" />
                  {isEmptyingTrash ? 'Emptying...' : 'Empty trash'}
                </Button>
              </span>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Empty trash</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all {items.length} item
                  {items.length !== 1 ? 's' : ''} in the trash. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onEmptyTrash}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete Forever
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Create dropdown (hide in trash view) */}
        {!isTrash && (
          <span className={!canEdit ? 'cursor-not-allowed' : ''}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  disabled={!canEdit}
                >
                  <Plus className="mr-2 size-4" />
                  New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? onCreateTable : undefined}
                  >
                    <Table className="mr-2 size-4" />
                    New Table
                  </DropdownMenuItem>
                </span>
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? onCreateLinkedTable : undefined}
                  >
                    <Link2 className="mr-2 size-4" />
                    New Linked Table
                  </DropdownMenuItem>
                </span>
                <DropdownMenuSeparator />
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? onCreateFolder : undefined}
                  >
                    <FolderPlus className="mr-2 size-4 fill-amber-500 text-amber-600" />
                    New Folder
                  </DropdownMenuItem>
                </span>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        )}
      </div>
    </div>
  );
}
